import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Badge,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Tooltip,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Fade,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  SoupKitchen as KitchenIcon,
  CheckCircle as ReadyIcon,
  AccessTime as TimeIcon,
  Notifications as NotifyIcon,
  VolumeUp as SoundIcon,
  VolumeOff as MuteIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  DoneAll as DoneAllIcon,
  TableRestaurant as TableIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';

const KITCHEN_STATIONS = ['all', 'main', 'grill', 'tandoor', 'bar', 'fryer', 'dessert'];

const STATUS_COLORS = {
  pending: '#ff9800',
  cooking: '#f44336',
  ready: '#4caf50',
  served: '#2196f3',
  billed: '#757575',
};

const ORDER_TIMEOUT = 25;

// ==================== ORDER CARD ====================
const OrderCard = memo(({ order, onMarkServed, onOpenDetail, timers }) => {
  const elapsedMinutes = Math.floor((new Date() - new Date(order.created_at)) / 60000);
  const priorityColor = elapsedMinutes > 20 ? '#d32f2f' : elapsedMinutes > 10 ? '#ff9800' : '#4caf50';
  const remainingTime = timers[order.id] || 0;
  const isTimerActive = order.status === 'cooking' || order.status === 'preparing' || order.status === 'pending';
  const timerPercentage = ORDER_TIMEOUT > 0 ? ((ORDER_TIMEOUT - remainingTime) / ORDER_TIMEOUT) * 100 : 0;
  
  const isValidTable = order.table_number && 
                       order.table_number !== 'N/A' && 
                       order.table_number !== 'undefined' && 
                       order.table_number !== 'null';

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pending',
      preparing: 'Cooking',
      cooking: 'Cooking',
      ready: 'Ready',
      served: 'Served',
      billed: 'Billed',
    };
    return labels[status] || status;
  };

  const allItemsReady = order.items?.every((item) => 
    item.kitchen_status === 'ready' || item.kitchen_status === 'served'
  );

  return (
    <Fade in={true} timeout={400}>
      <Card
        sx={{
          borderRadius: 3,
          border: `2px solid ${priorityColor}`,
          bgcolor: '#1a1a2e',
          color: '#fff',
          transition: 'all 0.3s',
          '&:hover': { boxShadow: `0 8px 32px ${priorityColor}30` },
          position: 'relative',
          overflow: 'visible',
          height: '100%',
        }}
      >
        {isTimerActive && (
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, borderRadius: '3px 3px 0 0', overflow: 'hidden' }}>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, timerPercentage)}
              sx={{
                height: 3,
                bgcolor: 'transparent',
                '& .MuiLinearProgress-bar': {
                  bgcolor: remainingTime < 5 ? '#f44336' : remainingTime < 10 ? '#ff9800' : '#4caf50',
                },
              }}
            />
          </Box>
        )}

        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#ff6b35', lineHeight: 1, fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
                #{order.id}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                KOT #{order.kot_number || 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              {isTimerActive && (
                <Chip
                  size="small"
                  icon={<TimerIcon fontSize="small" />}
                  label={remainingTime > 0 ? `${remainingTime}m` : 'Time Up!'}
                  sx={{
                    bgcolor: remainingTime < 5 ? '#f4433620' : remainingTime < 10 ? '#ff980020' : '#4caf5020',
                    color: remainingTime < 5 ? '#f44336' : remainingTime < 10 ? '#ff9800' : '#4caf50',
                    fontWeight: 700,
                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    height: 24,
                    mb: 0.5,
                  }}
                />
              )}
              <Chip
                size="small"
                icon={<TimeIcon fontSize="small" />}
                label={`${String(Math.floor(elapsedMinutes)).padStart(2, '0')}:${String(elapsedMinutes % 60).padStart(2, '0')}`}
                sx={{
                  bgcolor: `${priorityColor}20`,
                  color: priorityColor,
                  fontWeight: 700,
                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                  height: 22,
                }}
              />
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mt: 0.3, fontSize: { xs: '0.55rem', sm: '0.65rem' } }}>
                {new Date(order.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
            <Chip
              size="small"
              label={order.order_type?.replace('_', ' ').toUpperCase() || 'DINE IN'}
              sx={{
                bgcolor: order.order_type === 'dine_in' ? '#4caf5020' : order.order_type === 'takeaway' ? '#ff980020' : '#2196f320',
                color: order.order_type === 'dine_in' ? '#4caf50' : order.order_type === 'takeaway' ? '#ff9800' : '#2196f3',
                fontWeight: 600,
                fontSize: { xs: '0.55rem', sm: '0.65rem' },
                height: 22,
              }}
            />
            
            {isValidTable && (
              <Chip
                size="small"
                icon={<TableIcon fontSize="small" />}
                label={`Table ${order.table_number}`}
                sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: { xs: '0.55rem', sm: '0.65rem' }, height: 22 }}
              />
            )}
            
            {order.order_type === 'delivery' && (
              <Chip
                size="small"
                icon={<PersonIcon fontSize="small" />}
                label={order.customer_name || 'Delivery'}
                sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: { xs: '0.55rem', sm: '0.65rem' }, height: 22 }}
              />
            )}
            
            <Chip
              size="small"
              label={getStatusLabel(order.status)}
              sx={{
                bgcolor: `${STATUS_COLORS[order.status] || '#757575'}20`,
                color: STATUS_COLORS[order.status] || '#fff',
                fontWeight: 600,
                fontSize: { xs: '0.55rem', sm: '0.65rem' },
                height: 22,
              }}
            />
          </Box>

          <Box sx={{ mb: 1.5 }}>
            {order.items?.slice(0, 3).map((item, idx) => (
              <Paper
                key={idx}
                sx={{
                  mb: 0.5,
                  p: { xs: 1, sm: 1.5 },
                  borderRadius: 2,
                  bgcolor: item.kitchen_status === 'ready'
                    ? 'rgba(76, 175, 80, 0.1)'
                    : item.kitchen_status === 'cooking'
                      ? 'rgba(244, 67, 54, 0.1)'
                      : 'rgba(255, 255, 255, 0.05)',
                  border: `1px solid ${
                    item.kitchen_status === 'ready'
                      ? '#4caf50'
                      : item.kitchen_status === 'cooking'
                        ? '#f44336'
                        : 'rgba(255,255,255,0.1)'
                  }`,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#fff', fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                      {item.quantity}x {item.item_name}
                    </Typography>
                    {item.notes && (
                      <Typography variant="caption" sx={{ color: '#ff9800', display: 'block', fontSize: { xs: '0.55rem', sm: '0.65rem' } }}>
                        📝 {item.notes}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                    {item.kitchen_status === 'pending' && (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => onMarkServed(item.id, 'cooking')}
                        sx={{
                          bgcolor: '#f44336',
                          '&:hover': { bgcolor: '#d32f2f' },
                          minWidth: 0,
                          px: { xs: 1, sm: 1.5 },
                          py: 0.2,
                          fontSize: { xs: '0.55rem', sm: '0.65rem' },
                        }}
                      >
                        START
                      </Button>
                    )}
                    {item.kitchen_status === 'cooking' && (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => onMarkServed(item.id, 'ready')}
                        sx={{
                          bgcolor: '#4caf50',
                          '&:hover': { bgcolor: '#388e3c' },
                          minWidth: 0,
                          px: { xs: 1, sm: 1.5 },
                          py: 0.2,
                          fontSize: { xs: '0.55rem', sm: '0.65rem' },
                        }}
                      >
                        READY
                      </Button>
                    )}
                    {item.kitchen_status === 'ready' && (
                      <Chip
                        size="small"
                        icon={<ReadyIcon fontSize="small" />}
                        label="DONE"
                        color="success"
                        sx={{ fontSize: { xs: '0.55rem', sm: '0.65rem' }, height: 20 }}
                      />
                    )}
                  </Box>
                </Box>
              </Paper>
            ))}
            {order.items?.length > 3 && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mt: 0.5, textAlign: 'center' }}>
                +{order.items.length - 3} more items
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {allItemsReady && order.status !== 'served' && order.status !== 'billed' && (
              <Button
                fullWidth
                variant="contained"
                startIcon={<DoneAllIcon />}
                onClick={() => onMarkServed(order.id, 'served')}
                sx={{
                  bgcolor: '#2196f3',
                  '&:hover': { bgcolor: '#1976d2' },
                  fontWeight: 700,
                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                  py: { xs: 0.5, sm: 0.8 },
                }}
              >
                MARK SERVED
              </Button>
            )}
            <Button
              fullWidth
              variant="outlined"
              onClick={() => onOpenDetail(order)}
              sx={{
                borderColor: 'rgba(255,255,255,0.3)',
                color: 'rgba(255,255,255,0.7)',
                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                py: { xs: 0.5, sm: 0.8 },
              }}
            >
              DETAILS
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Fade>
  );
});

// ==================== MAIN COMPONENT ====================
const KitchenDisplay = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [orders, setOrders] = useState([]);
  const [pendingKOTs, setPendingKOTs] = useState([]);
  const [selectedStation, setSelectedStation] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetailOpen, setOrderDetailOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [timers, setTimers] = useState({});

  const audioRef = useRef(null);
  const intervalRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const isMounted = useRef(true);

  const showMessage = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const getAPI = useCallback(() => {
    if (typeof window !== 'undefined' && window.electronAPI) {
      return window.electronAPI;
    }
    return null;
  }, []);

  const getStorage = useCallback(async () => {
    try {
      const module = await import('../database/storage');
      const storage = module.default || module;
      await storage.init();
      return storage;
    } catch (e) {
      console.warn('[Kitchen] Storage not available:', e);
      return null;
    }
  }, []);

  // ==================== LOAD DATA ====================
  const loadKitchenData = useCallback(async () => {
    try {
      console.log('[Kitchen] Loading data...');
      const api = getAPI();
      let allOrders = [];
      let fullOrders = [];

      if (!api) {
        const storage = await getStorage();
        if (!storage) throw new Error('Storage not available');
        allOrders = await storage.getOrders ? await storage.getOrders({}) : [];
        
        for (const order of allOrders || []) {
          if (!order || !order.id) continue;
          const fullOrder = storage.getOrderById ? await storage.getOrderById(order.id) : null;
          if (fullOrder) {
            if (!fullOrder.table_number || fullOrder.table_number === 'undefined' || fullOrder.table_number === 'null') {
              fullOrder.table_number = 'N/A';
            }
            fullOrders.push(fullOrder);
          }
        }
      } else {
        allOrders = await api.getOrders({});
        for (const order of allOrders || []) {
          if (!order || !order.id) continue;
          const fullOrder = await api.getOrderById(order.id);
          if (fullOrder) {
            if (!fullOrder.table_number || fullOrder.table_number === 'undefined' || fullOrder.table_number === 'null') {
              fullOrder.table_number = 'N/A';
            }
            fullOrders.push(fullOrder);
          }
        }
      }

      console.log('[Kitchen] Loaded orders:', fullOrders.length);

      if (isMounted.current) {
        setOrders(fullOrders);
        setLastUpdated(new Date());
        setLoading(false);
      }

      if (soundEnabled && fullOrders.length > 0 && isMounted.current) {
        audioRef.current?.play().catch(() => {});
      }

    } catch (err) {
      console.error('[Kitchen] Load error:', err);
      if (isMounted.current) {
        showMessage('❌ Data load nahi ho saki!', 'error');
        setLoading(false);
      }
    }
  }, [getAPI, getStorage, soundEnabled, showMessage]);

  // ==================== MARK SERVED ====================
  const markAllServed = useCallback(async (orderId, status = 'served') => {
    try {
      const api = getAPI();
      
      if (status === 'cooking' || status === 'ready') {
        if (!api) {
          const storage = await getStorage();
          if (storage && storage.updateItemKitchenStatus) {
            await storage.updateItemKitchenStatus(orderId, status);
          }
        } else {
          await api.updateItemKitchenStatus(orderId, status);
        }
        if (isMounted.current) {
          showMessage(`✅ Item status updated to ${status}`, 'success');
          await loadKitchenData();
        }
        return;
      }
      
      if (!api) {
        const storage = await getStorage();
        if (storage && storage.updateOrderStatus) {
          await storage.updateOrderStatus(orderId, 'served');
        }
      } else {
        await api.updateOrderStatus(orderId, 'served');
      }
      if (isMounted.current) {
        showMessage(`✅ Order #${orderId} marked as served!`, 'success');
        await loadKitchenData();
      }
    } catch (err) {
      console.error('[Kitchen] Serve error:', err);
      if (isMounted.current) {
        showMessage(`❌ Failed to serve: ${err.message}`, 'error');
      }
    }
  }, [getAPI, getStorage, showMessage, loadKitchenData]);

  const openOrderDetail = (order) => {
    setSelectedOrder(order);
    setOrderDetailOpen(true);
  };

  // ==================== TIMER ====================
  useEffect(() => {
    timerIntervalRef.current = setInterval(() => {
      if (!isMounted.current) return;
      setTimers(prev => {
        const newTimers = {};
        orders.forEach(order => {
          const elapsed = Math.floor((new Date() - new Date(order.created_at)) / 60000);
          const remaining = Math.max(0, ORDER_TIMEOUT - elapsed);
          newTimers[order.id] = remaining;
        });
        return newTimers;
      });
    }, 1000);
    return () => clearInterval(timerIntervalRef.current);
  }, [orders]);

  // ==================== EFFECTS ====================
  useEffect(() => {
    audioRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLHHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUarm7blmFgU7k9n1unEiBC13yO/eizEIHWq+8+OWT');
  }, []);

  // ✅ FIX: EMPTY array - NO infinite loop!
  useEffect(() => {
    console.log('[Kitchen] Mounted - loading initial data');
    loadKitchenData();
    intervalRef.current = setInterval(() => {
      loadKitchenData();
    }, 10000);
    return () => {
      isMounted.current = false;
      clearInterval(intervalRef.current);
      clearInterval(timerIntervalRef.current);
    };
  }, []); // ✅ EMPTY array

  // ==================== FILTERED ORDERS ====================
  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const stationMatch = selectedStation === 'all' || order.items?.some((item) => 
        item.kitchen_station === selectedStation || item.kitchen_station === 'main'
      );
      const statusMatch = selectedStatus === 'all' || order.status === selectedStatus;
      return stationMatch && statusMatch;
    });
  }, [orders, selectedStation, selectedStatus]);

  // ==================== RENDER ====================
  return (
    <Box sx={{ 
      height: 'calc(100vh - 120px)', 
      display: 'flex', 
      flexDirection: 'column', 
      bgcolor: '#0a0a0a',
    }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ borderRadius: 2, fontWeight: 600 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* HEADER */}
      <Paper
        sx={{
          p: { xs: 1, sm: 1.5 },
          borderRadius: 0,
          bgcolor: '#1a1a2e',
          color: '#fff',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 1, sm: 1.5 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <KitchenIcon sx={{ color: '#ff6b35', fontSize: { xs: 28, sm: 32 } }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', lineHeight: 1.2, fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
              KITCHEN DISPLAY
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
              کچن ڈسپلے | Live Orders
            </Typography>
          </Box>
          <Badge
            badgeContent={orders.length}
            color="error"
            sx={{
              '& .MuiBadge-badge': {
                fontSize: { xs: '0.7rem', sm: '0.9rem' },
                fontWeight: 700,
                minWidth: { xs: 24, sm: 28 },
                height: { xs: 24, sm: 28 },
                borderRadius: '50%',
              },
            }}
          >
            <Avatar sx={{ bgcolor: '#ff6b35', width: { xs: 32, sm: 36 }, height: { xs: 32, sm: 36 } }}>
              <NotifyIcon fontSize="small" />
            </Avatar>
          </Badge>
        </Box>

        <Box sx={{ 
          display: 'flex', 
          gap: 0.5, 
          overflowX: 'auto',
          flexWrap: { xs: 'nowrap', sm: 'wrap' },
        }}>
          <ToggleButtonGroup
            value={selectedStation}
            exclusive
            onChange={(e, val) => val && setSelectedStation(val)}
            size="small"
            sx={{
              flexShrink: 0,
              '& .MuiToggleButton-root': {
                color: 'rgba(255,255,255,0.6)',
                borderColor: 'rgba(255,255,255,0.2)',
                textTransform: 'capitalize',
                px: { xs: 1, sm: 2 },
                py: { xs: 0.3, sm: 0.5 },
                fontSize: { xs: '0.6rem', sm: '0.8rem' },
              },
              '& .Mui-selected': {
                bgcolor: '#ff6b35 !important',
                color: '#fff !important',
              },
            }}
          >
            {KITCHEN_STATIONS.slice(0, isMobile ? 4 : 7).map((station) => (
              <ToggleButton key={station} value={station}>
                {station === 'all' ? 'All' : station}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <ToggleButtonGroup
            value={selectedStatus}
            exclusive
            onChange={(e, val) => val && setSelectedStatus(val)}
            size="small"
            sx={{
              flexShrink: 0,
              '& .MuiToggleButton-root': {
                color: 'rgba(255,255,255,0.6)',
                borderColor: 'rgba(255,255,255,0.2)',
                fontSize: { xs: '0.6rem', sm: '0.8rem' },
                px: { xs: 1, sm: 1.5 },
                py: { xs: 0.3, sm: 0.5 },
              },
              '& .Mui-selected': {
                bgcolor: '#4caf50 !important',
                color: '#fff !important',
              },
            }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="pending">Pending</ToggleButton>
            <ToggleButton value="cooking">Cooking</ToggleButton>
            <ToggleButton value="ready">Ready</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
          <Tooltip title={soundEnabled ? 'Mute' : 'Unmute'}>
            <IconButton
              onClick={() => setSoundEnabled(!soundEnabled)}
              sx={{ color: soundEnabled ? '#ff6b35' : 'rgba(255,255,255,0.5)', p: { xs: 0.5, sm: 0.8 } }}
              size={isMobile ? 'small' : 'medium'}
            >
              {soundEnabled ? <SoundIcon /> : <MuteIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={loadKitchenData} sx={{ color: 'rgba(255,255,255,0.7)', p: { xs: 0.5, sm: 0.8 } }} size={isMobile ? 'small' : 'medium'}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace', fontSize: { xs: '0.55rem', sm: '0.7rem' }, flexShrink: 0 }}>
          Updated: {lastUpdated.toLocaleTimeString()}
        </Typography>
      </Paper>

      {/* ORDERS GRID */}
      <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 1, sm: 2 } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <LinearProgress sx={{ width: '100%', bgcolor: '#ff6b35' }} />
          </Box>
        ) : filteredOrders.length > 0 ? (
          <Grid container spacing={1.5}>
            {filteredOrders.map((order) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={order.id}>
                <OrderCard 
                  order={order}
                  onMarkServed={markAllServed}
                  onOpenDetail={openOrderDetail}
                  timers={timers}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'rgba(255,255,255,0.3)',
              py: 8,
            }}
          >
            <KitchenIcon sx={{ fontSize: { xs: 60, sm: 80 }, mb: 2, opacity: 0.3 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
              No Active Orders
            </Typography>
            <Typography variant="body1" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              کوئی زیر التواء آرڈر نہیں
            </Typography>
            <Typography variant="caption" sx={{ mt: 1, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
              Waiting for new KOTs...
            </Typography>
          </Box>
        )}
      </Box>

      {/* STATUS BAR */}
      <Paper
        sx={{
          p: { xs: 0.5, sm: 1 },
          borderRadius: 0,
          bgcolor: '#1a1a2e',
          color: '#fff',
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        {[
          { label: 'Pending', count: orders.filter((o) => o.status === 'pending').length, color: '#ff9800' },
          { label: 'Cooking', count: orders.filter((o) => o.status === 'cooking' || o.status === 'preparing').length, color: '#f44336' },
          { label: 'Ready', count: orders.filter((o) => o.status === 'ready').length, color: '#4caf50' },
          { label: 'Total Items', count: orders.reduce((sum, o) => sum + (o.items?.length || 0), 0), color: '#ff6b35' },
        ].map((stat) => (
          <Box key={stat.label} sx={{ textAlign: 'center', p: { xs: 0.5, sm: 1 } }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: stat.color, fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
              {stat.count}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: { xs: '0.55rem', sm: '0.65rem' } }}>
              {stat.label}
            </Typography>
          </Box>
        ))}
      </Paper>

      {/* ORDER DETAIL DIALOG */}
      <Dialog
        open={orderDetailOpen}
        onClose={() => setOrderDetailOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#1a1a2e', color: '#fff', borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#ff6b35', fontSize: { xs: '1rem', sm: '1.1rem' } }}>
          Order #{selectedOrder?.id} Details
          <IconButton
            onClick={() => setOrderDetailOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'rgba(255,255,255,0.5)' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                  Type: <strong style={{ color: '#fff' }}>{selectedOrder.order_type?.replace('_', ' ').toUpperCase() || 'DINE IN'}</strong>
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                  Table: <strong style={{ color: '#fff' }}>
                    {selectedOrder.table_number && 
                     selectedOrder.table_number !== 'N/A' && 
                     selectedOrder.table_number !== 'undefined' && 
                     selectedOrder.table_number !== 'null' 
                       ? selectedOrder.table_number 
                       : 'N/A'}
                  </strong>
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                  Time: <strong style={{ color: '#fff' }}>{new Date(selectedOrder.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</strong>
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                  Status: <Chip size="small" label={selectedOrder.status} sx={{ ml: 1, bgcolor: `${STATUS_COLORS[selectedOrder.status] || '#757575'}20`, color: STATUS_COLORS[selectedOrder.status] || '#fff' }} />
                </Typography>
                {selectedOrder.customer_name && (
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                    Customer: <strong style={{ color: '#fff' }}>{selectedOrder.customer_name}</strong>
                  </Typography>
                )}
              </Box>

              <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#fff', mb: 1, fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                Items ({selectedOrder.items?.length || 0})
              </Typography>
              <List dense>
                {selectedOrder.items?.map((item, idx) => (
                  <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                            {item.quantity}x {item.item_name}
                          </Typography>
                          <Chip
                            size="small"
                            label={item.kitchen_status?.toUpperCase() || 'PENDING'}
                            sx={{
                              bgcolor: `${STATUS_COLORS[item.kitchen_status] || '#757575'}20`,
                              color: STATUS_COLORS[item.kitchen_status] || '#fff',
                              fontSize: { xs: '0.55rem', sm: '0.65rem' },
                              height: 20,
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        item.notes && (
                          <Typography variant="caption" sx={{ color: '#ff9800', fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                            {item.notes}
                          </Typography>
                        )
                      }
                    />
                  </ListItem>
                ))}
              </List>

              {selectedOrder.notes && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'rgba(255, 152, 0, 0.1)', borderRadius: 2 }}>
                  <Typography variant="caption" sx={{ color: '#ff9800', fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                    📝 {selectedOrder.notes}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={() => setOrderDetailOpen(false)} variant="outlined" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)', width: { xs: '100%', sm: 'auto' } }}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e55a2b' }, width: { xs: '100%', sm: 'auto' } }}
            onClick={() => showMessage('🖨️ Printing KOT...', 'info')}
          >
            Print KOT
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KitchenDisplay;